import { initializeApp, getApps, type FirebaseApp } from 'firebase/app'
import {
  getAnalytics,
  isSupported,
  logEvent,
  setAnalyticsCollectionEnabled,
  setConsent,
  type Analytics
} from 'firebase/analytics'

const firebaseConfig = {
  apiKey: 'AIzaSyD_RHLZDqAqjCdyaVEiDkFPEz4xJ7OdWGI',
  authDomain: 'pawsight-web.firebaseapp.com',
  projectId: 'pawsight-web',
  storageBucket: 'pawsight-web.firebasestorage.app',
  messagingSenderId: '159916070545',
  appId: '1:159916070545:web:e2e014127c58f53676d130',
  measurementId: 'G-3E77HSB9J4'
}

type LandingAnalyticsEventName =
  | 'landing_page_view'
  | 'landing_navigation_click'
  | 'landing_app_store_click'
  | 'landing_google_play_click'
  | 'landing_footer_link_click'

type LandingAnalyticsParams = Record<string, string | number | boolean | undefined>

export type LandingAnalyticsConsent = boolean | null

const ANALYTICS_CONSENT_STORAGE_KEY = 'pawsight_analytics_consent_v1'

type StoredAnalyticsConsent = {
  analytics: boolean
  updatedAt: string
  version: 1
}

let firebaseApp: FirebaseApp | null = null
let analyticsInstance: Analytics | null = null
let analyticsInitPromise: Promise<Analytics | null> | null = null

/** Reads the visitor's locally stored optional-analytics choice. */
export const getLandingAnalyticsConsent = (): LandingAnalyticsConsent => {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const storedValue = window.localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY)
    if (!storedValue) {
      return null
    }

    const parsedValue = JSON.parse(storedValue) as Partial<StoredAnalyticsConsent>
    return typeof parsedValue.analytics === 'boolean' ? parsedValue.analytics : null
  } catch {
    return null
  }
}

/** Removes accessible Google Analytics first-party cookies after consent is withdrawn. */
const clearAnalyticsCookies = (): void => {
  if (typeof document === 'undefined') {
    return
  }

  const hostname = window.location.hostname
  const cookieDomains = ['', hostname, `.${hostname}`]

  document.cookie.split(';').forEach((cookie) => {
    const cookieName = cookie.split('=')[0]?.trim()
    if (!cookieName?.startsWith('_ga')) {
      return
    }

    cookieDomains.forEach((domain) => {
      const domainAttribute = domain ? `; domain=${domain}` : ''
      document.cookie = `${cookieName}=; Max-Age=0; path=/${domainAttribute}; SameSite=Lax`
    })
  })
}

/**
 * Returns the shared Firebase app instance for the landing page.
 */
const getFirebaseApp = (): FirebaseApp => {
  if (firebaseApp) {
    return firebaseApp
  }

  firebaseApp = getApps()[0] ?? initializeApp(firebaseConfig)
  return firebaseApp
}

/**
 * Initializes Firebase Analytics only in supported browser environments.
 */
export const initializeFirebaseAnalytics = async (): Promise<Analytics | null> => {
  if (getLandingAnalyticsConsent() !== true) {
    return null
  }

  if (analyticsInstance) {
    return analyticsInstance
  }

  if (analyticsInitPromise) {
    return analyticsInitPromise
  }

  analyticsInitPromise = (async () => {
    if (typeof window === 'undefined') {
      return null
    }

    const supported = await isSupported().catch(() => false)
    if (!supported) {
      return null
    }

    analyticsInstance = getAnalytics(getFirebaseApp())
    return analyticsInstance
  })()

  return analyticsInitPromise
}

/** Persists and applies the visitor's optional-analytics choice. */
export const setLandingAnalyticsConsent = async (enabled: boolean): Promise<void> => {
  if (typeof window === 'undefined') {
    return
  }

  const storedConsent: StoredAnalyticsConsent = {
    analytics: enabled,
    updatedAt: new Date().toISOString(),
    version: 1
  }
  window.localStorage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, JSON.stringify(storedConsent))

  if (!enabled) {
    setConsent({
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied'
    })
    if (analyticsInstance) {
      setAnalyticsCollectionEnabled(analyticsInstance, false)
    }
    clearAnalyticsCookies()
    return
  }

  setConsent({
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied'
  })
  const analytics = await initializeFirebaseAnalytics()
  if (!analytics) {
    return
  }

  setAnalyticsCollectionEnabled(analytics, true)
}

/**
 * Logs a landing-page analytics event when Firebase Analytics is available.
 */
export const trackLandingEvent = async (
  eventName: LandingAnalyticsEventName,
  params: LandingAnalyticsParams = {}
): Promise<void> => {
  const analytics = await initializeFirebaseAnalytics()
  if (!analytics) {
    return
  }

  logEvent(analytics, eventName, params)
}
